import clsx from 'clsx';
import numeral from 'numeral';
import React from 'react';

import styles from './Input.module.css';

interface Props {
  className?: string;
  inputClassName?: string;
  type: InputType;
  name?: string;
  textarea?: boolean;
  inputType?: InputType;
  value: string;
  label?: string;
  decimalLength?: number;
  format?: boolean;
  readOnly?: boolean;
  error?: boolean;
  prefix?: string;
  prefixSpace?: PREFIX_SPACE;
  prefixMobile?: boolean;
  suffix?: string;
  suffixfull?: string;
  onChange?: (e: string) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => boolean;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => boolean;
  onKeyPress?: () => void;
  onKeyDown?: () => void;
  onClick?: () => void;
}

export const Input: React.FC<Props> = props => {
  const {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    type = InputType.TEXT,
    name,
    label,
    format,
    decimalLength = 8,
    value,
    readOnly,
    onKeyPress,
    onKeyDown,
    onClick,
  } = props;
  const [focused, setFocused] = React.useState(false);

  const getOutputValue = (formattedValue: string) => {
    let outputValue: number | string | undefined = formattedValue;
    if (outputValue !== '') {
      switch (type) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        case InputType.INTEGER:
          outputValue = numeral(outputValue).value() ?? undefined;
          if (
            outputValue === null ||
            outputValue === undefined ||
            Number.isNaN(outputValue)
          ) {
            return null;
          }
          break;
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        case InputType.REAL:
          {
            outputValue = numeral(outputValue).value() ?? undefined;
            if (outputValue === null || Number.isNaN(outputValue!)) {
              return null;
            }
            const decimalFix = formattedValue.endsWith('.') ? '.' : '';
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            outputValue = getFormattedValue(
              `${outputValue ?? ''}${decimalFix}`,
            );
            outputValue = outputValue.replace(',', '');
          }
          break;
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        case InputType.PHONE:
          if (outputValue.length > 0) {
            const numbersOnly = outputValue.startsWith('+')
              ? outputValue.substring(1)
              : outputValue;
            for (let i = numbersOnly.length; i-- > 0; ) {
              if (numbersOnly[i] < '0' || numbersOnly[i] > '9') {
                return null;
              }
            }
          }
          break;
        default:
          break;
      }
    }

    return outputValue.toString();
  };

  const getFormattedValue = (valueFormat: string) => {
    if (format === false) {
      return valueFormat;
    }

    if (valueFormat === '') {
      return '';
    }
    switch (type) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      case InputType.INTEGER:
        return numeral(valueFormat).format('0,0');
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      case InputType.REAL: {
        const decimalFix = valueFormat.endsWith('.') ? '.' : '';
        return `${numeral(valueFormat).format(
          `0,0.[${'0'.repeat(decimalLength)}]`,
        )}${decimalFix}`;
      }
      default:
        return valueFormat;
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const outputValue = getOutputValue(e.target.value);
    if (outputValue === null) {
      return;
    }

    if (props.onChange !== undefined) {
      props.onChange(outputValue);
    }
  };

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (props.onFocus === undefined || !props.onFocus(e)) {
      setFocused(true);
    }
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (props.onBlur === undefined || !props.onBlur(e)) {
      setFocused(false);
    }
  };

  const getInputType = (inputType: InputType) => {
    // eslint-disable-next-line sonarjs/no-small-switch
    switch (inputType) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      case InputType.PASSWORD:
        return 'password';
      default:
        return 'text';
    }
  };

  const renderInputNode = () => {
    if (props.textarea === false) {
      return (
        <input
          className={
            props.inputClassName != null
              ? props.inputClassName
              : styles.customTextbox
          }
          type={getInputType(type)}
          name={name}
          placeholder={label}
          value={value}
          readOnly={readOnly}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyPress={onKeyPress}
          onKeyDown={onKeyDown}
          onClick={onClick}
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          autoComplete={type === InputType.PASSWORD ? 'off' : undefined}
        />
      );
    }

    return <textarea />;
  };

  const { className = '', error = false } = props;
  return (
    <div className={props.className}>
      <div
        className={`input-component ${className}
          ${clsx({ Focused: focused, Error: error })}`}
        title={props.label}
      >
        {renderInputNode()}
      </div>
    </div>
  );
};

export enum PREFIX_SPACE {
  PREFIX_SPACE_DEFAULT = '',
  PREFIX_SPACE_NONE = 'PrefixNoSpace',
}

export enum InputType {
  INTEGER = 1,
  REAL = 2,
  TEXT = 3,
  PHONE = 4,
  PASSWORD = 5,
}

Input.defaultProps = {
  className: '',
  name: '',
  type: InputType.TEXT,
  textarea: false,
  inputType: InputType.TEXT,
  label: '',
  decimalLength: 8,
  format: true,
  readOnly: false,
  error: false,
  prefix: '',
  prefixSpace: PREFIX_SPACE.PREFIX_SPACE_DEFAULT,
  prefixMobile: true,
  suffix: '',
  suffixfull: '',
};
